// encounter-stats-update.ts — handler for the 'encounter_stats_update' fast-path frame
// (2026-06-12, action-latency Lever A follow-up — plans/action-latency-recompile-plan.md).
//
// PHP publisher: update_encounter_stats (state_propagation.php) broadcasts the changed
// encounters-table columns at MUTATION time. The canonical encounter_update only arrives
// after the post-action recompile fan-out (PAI rebuilds the encounter first with every
// creature cold — 20s+ on a full encounter), so scalar-driven UI (combat shape, turn
// marker, round counter) lagged every action by that long. This handler patches the
// cached blob's scalars in place and re-runs ONLY the scalar-driven sync pieces of
// handleEncounterUpdate; the canonical push reconciles full encounter state later.

import type { EncounterArr } from '../shared/array-types';
import { encounterCreatureField, encounterField, setEncounterField } from '../shared/array-types';
import { lightTypeName, weatherTypeName } from '../shared/env-types';
import type { WSMessage } from './types';
import { traceEncounterTruth } from './encounter-truth-trace';
import { reconcileEncounterPermanentStatuses } from './permanent-status-environment';

// Encounters-table scalars whose mutation-time value is authoritative over a later (possibly
// stale) full encounter_update. Kept in lockstep with the same lists in encounter-update.ts.
// turn_phase / pending_main_turn_encounter_creature_id: Legendary Turn interlude state
// (plans/legendary-turns-plan.md) — published in the same frame as the turn pointer.
const FT_ENC_SCALAR_AUTH_KEYS = ['in_combat', 'current_round', 'current_encounter_creature_id_turn', 'turn_phase', 'pending_main_turn_encounter_creature_id'];
// Catalog-shelled authority keys (env/light/weather hold {items:{item_id}} in the bucket). Same
// mutation-time authority as the bare scalars above, but reconciled via setEncounterField so the
// shell — and its item_name display carrier — survives the override.
const FT_ENC_CATALOG_AUTH_KEYS = ['encounter_light', 'encounter_weather'];

type EncounterStatsUpdateFrame = Extract<WSMessage, { type: 'encounter_stats_update' }>;

function publishEncounterTurnState(
    encounterId: string,
    encounterArr: EncounterArr | null,
    updates: Record<string, unknown>,
): void {
    if (typeof window.dispatchEvent !== 'function') return;
    if (
        !Object.prototype.hasOwnProperty.call(updates, 'current_encounter_creature_id_turn')
        && !Object.prototype.hasOwnProperty.call(updates, 'in_combat')
        && !Object.prototype.hasOwnProperty.call(updates, 'turn_phase')
    ) return;

    const value = (key: string): unknown => Object.prototype.hasOwnProperty.call(updates, key)
        ? updates[key]
        : encounterField(encounterArr, key);
    window.dispatchEvent(new CustomEvent('ft:encounter-turn-state', {
        detail: {
            encounterId,
            encounterArr,
            currentTurnId: String(value('current_encounter_creature_id_turn') ?? '').trim(),
            inCombat: parseInt(String(value('in_combat') ?? '0'), 10) === 1,
            combatStateAsserted: Object.prototype.hasOwnProperty.call(updates, 'in_combat'),
            turnPhase: String(value('turn_phase') ?? 'main'),
            source: 'encounter-stats-update',
        },
    }));
}

async function handleEncounterStatsUpdate(parsedData: EncounterStatsUpdateFrame): Promise<void> {
    const encounterId = parsedData?.encounter_id;
    const updates = parsedData?.updates;
    if (!encounterId || !updates || typeof updates !== 'object') {
        ftDebugLog('ft3d encounter', { warn: 'bad encounter_stats_update frame', parsedData });
        return;
    }
    // A DEATH-OF-THE-TURN-HOLDER boundary (the ability commit's death tail, Session A2 4c) names the holder it ended and its successor: it
    // applies only while this page still shows one of them — a delayed frame never moves a newer pointer back (Codex review of the
    // review-#6 folds: the guard in applyTurnTransition was bypassed by this relay).
    const boundary = (parsedData as { turn_boundary?: { dying_id?: unknown; next_id?: unknown } }).turn_boundary;
    if (boundary && typeof boundary === 'object' && window.FateTide?.encounterCache instanceof Map) {
        const shown = String(encounterField(window.FateTide.encounterCache.get(encounterId) as EncounterArr | null, 'current_encounter_creature_id_turn') ?? '').toUpperCase();
        const allowed = [boundary.dying_id, boundary.next_id].map(id => String(id ?? '').toUpperCase()).filter(Boolean);
        // an EMPTY pointer (combat ended meanwhile) is not one of them either (Codex review)
        if (allowed.length && !allowed.includes(shown)) {
            ftDebugLog('ft3d encounter', { statsFrameStaleBoundary: { shown, allowed } });
            return;
        }
    }

    const hasLightUpdate = Object.prototype.hasOwnProperty.call(updates, 'encounter_light');
    const hasWeatherUpdate = Object.prototype.hasOwnProperty.call(updates, 'encounter_weather');
    const encounterLightId = hasLightUpdate && typeof updates.encounter_light === 'string' && updates.encounter_light
        ? updates.encounter_light
        : null;
    const encounterWeatherId = hasWeatherUpdate && typeof updates.encounter_weather === 'string' && updates.encounter_weather
        ? updates.encounter_weather
        : null;
    const cachedEncounter = window.FateTide?.encounterCache instanceof Map
        ? window.FateTide.encounterCache.get(encounterId)
        : null;
    const finishTrace = traceEncounterTruth('encounter-stats-update', encounterId, { keys: Object.keys(updates) }, { encounters: updates });
    const permanentEnvironmentChanged = !!cachedEncounter && ['encounter_light', 'encounter_weather', 'encounter_environment',
        'encounter_sun', 'encounter_sun_cycle_mode'].some(key => Object.prototype.hasOwnProperty.call(updates, key)
            && String(encounterField(cachedEncounter, key) ?? '') !== String(updates[key] ?? ''));

    // The compact frame is mutation-time authority. Patch the retained encounter FIRST so every
    // projection (including containerless FT3D) reads the backend value when notified below.
    if (cachedEncounter && hasLightUpdate) {
        setEncounterField(cachedEncounter, 'encounter_light', encounterLightId, lightTypeName(encounterLightId) || undefined);
    }
    if (cachedEncounter && hasWeatherUpdate) {
        setEncounterField(cachedEncounter, 'encounter_weather', encounterWeatherId, weatherTypeName(encounterWeatherId) || undefined);
    }

    const publishEnvironmentApplied = (encounterArr: EncounterArr | null): void => {
        if (!hasLightUpdate && !hasWeatherUpdate) return;
        const detail: {
            encounterId: string;
            encounterArr: EncounterArr | null;
            encounterLightId?: string | null;
            encounterWeatherId?: string | null;
        } = { encounterId, encounterArr };
        if (hasLightUpdate) detail.encounterLightId = encounterLightId;
        if (hasWeatherUpdate) detail.encounterWeatherId = encounterWeatherId;
        window.dispatchEvent(new CustomEvent('ft:encounter-lighting-changed', {
            detail,
        }));
    };

    if (!cachedEncounter) {
        publishEnvironmentApplied(cachedEncounter as EncounterArr | null);
        publishEncounterTurnState(encounterId, cachedEncounter as EncounterArr | null, updates);
        ftDebugLog('ft3d encounter', { note: 'stats frame: encounter cache not ready', encounterId });
        return;
    }
    const encounterArr: EncounterArr = cachedEncounter as EncounterArr;

	// Initiative rolls finish assigning every creature before this scalar frame is
	// published. Patch that compact id=>value map into the cached roster now; without
	// it buildCombatWidget enters combat against stale pre-roll values and cannot
	// visibly reorder until the expensive canonical encounter rebuild arrives.
	let initiativeChanged = false;
	const initiativeUpdates = parsedData.initiative_updates;
	if (initiativeUpdates && typeof initiativeUpdates === 'object') {
		const byId = new Map(
			Object.entries(initiativeUpdates).map(([id, value]) => [id.toUpperCase(), value]),
		);
		for (const row of encounterArr.encounter_creatures_arr ?? []) {
			const ec = row?.encounter_creatures;
			const id = encounterCreatureField<string>(ec, 'encounter_creature_id');
			if (!ec || !id) continue;
			const next = byId.get(id.toUpperCase());
			if (next === undefined || !Number.isFinite(next)) continue;
			const previous = encounterCreatureField<number | string>(ec, 'creature_initiative');
			if (Number(previous ?? 0) !== next) initiativeChanged = true;
			(ec as Record<string, unknown>).creature_initiative = next;
		}
	}

    // Patch each scalar into the slot encounterField() reads (encounters bucket first,
    // legacy root otherwise). Catalog-shelled slots (env/light/weather hold
    // {items:{item_id}} nests) are NOT patched — a bare UUID can't faithfully replace
    // the shell (name reads would break); the canonical push reconciles those keys.
    const changedKeys: string[] = [];
    for (const [key, value] of Object.entries(updates)) {
        const oldVal = encounterField(encounterArr, key);
        const bucket = encounterArr.encounters;
        if (bucket && typeof bucket === 'object' && key in bucket) {
            const slot = bucket[key];
            if (slot !== null && typeof slot === 'object' && (slot as Record<string, unknown>).items) {
                ftDebugLog('ft3d encounter', { statsFrameSkipShelledKey: key });
                continue;
            }
            bucket[key] = value;
        } else {
            encounterArr[key] = value;
        }
        if (String(oldVal ?? '') !== String(value ?? '')) changedKeys.push(key);
    }

    // ── Instant map light/weather, written THROUGH to the blob (single source of truth) ──
    // The catalog-shelled slots (encounters.encounter_light = {items:{...}}) are skipped by the bare
    // patch loop above (and stay out of changedKeys, which drives combat-shape rebuilds). But THIS
    // frame carries the authoritative mutation-time UUID, ahead of the (possibly stale) canonical
    // encounter_update. Write it INTO the cached blob's shell via setEncounterField — preserving the
    // item_name carrier — so the blob/cache itself is the freshest truth, then apply to the map by
    // reading that now-current blob. The scalar-authority record below + the catalog reconcile in
    // handleEncounterUpdate keep a later stale full push from reverting it.
    if (Object.prototype.hasOwnProperty.call(updates, 'encounter_light')) {
        setEncounterField(encounterArr, 'encounter_light', encounterLightId, lightTypeName(encounterLightId) || undefined);
    }
    if (hasWeatherUpdate) {
        setEncounterField(encounterArr, 'encounter_weather', encounterWeatherId, weatherTypeName(encounterWeatherId) || undefined);
    }
    if (window.FateTide?.encounterCache instanceof Map) {
        window.FateTide.encounterCache.set(encounterId, encounterArr);
    }
    finishTrace?.();
    publishEnvironmentApplied(encounterArr);
    publishEncounterTurnState(encounterId, encounterArr, updates);

    // ── Mutation-time scalar authority (plans/combat-widget-revert-on-exit.md, Option A) ──
    // in_combat / current_round / current_encounter_creature_id_turn drive the combat-widget
    // shape + turn marker. This instant frame is published at MUTATION time on an ordered
    // channel, so for these scalars it is the only mutation-ORDERED truth: a later full
    // encounter_update can carry a STALE snapshot (ft_encounter_next_version is a publish-time
    // INCR, so an overlapping/earlier recompile that snapshotted the pre-mutation value can
    // publish LAST and revert the shape). update_encounter_stats is the sole live write
    // chokepoint for these columns, so the values it just asserted ARE the freshest truth.
    // Record them so handleEncounterUpdate can override a disagreeing (stale) full push for a
    // bounded window. Record from `updates` (the asserted write), and BEFORE the
    // changedKeys early-return so a redundant-but-confirming frame still refreshes the TTL.
    {
        window.FateTide = window.FateTide || {};
        const authStore = (window.FateTide.encounterScalarAuthority =
            window.FateTide.encounterScalarAuthority || {});
        let entry = authStore[encounterId];
        for (const k of [...FT_ENC_SCALAR_AUTH_KEYS, ...FT_ENC_CATALOG_AUTH_KEYS]) {
            if (!Object.prototype.hasOwnProperty.call(updates, k)) continue;
            if (!entry) entry = authStore[encounterId] = { values: {}, ts: 0 };
            entry.values[k] = updates[k];
        }
        if (entry) entry.ts = Date.now();
    }

    if (permanentEnvironmentChanged) await reconcileEncounterPermanentStatuses(encounterArr);

    // A canonical encounter_update can win the race and put these exact scalar values into the
    // cache before this mutation-time frame arrives. The controls may still represent the older
    // state, because canonical adoption deliberately does not rebuild the GM widget. Treat the
    // presence of a scalar-driven UI key as repaint authority even when the cached value already
    // matches; otherwise initiative can show the combat strip while leaving no Exit Combat button.
    const asserted = (key: string): boolean => Object.prototype.hasOwnProperty.call(updates, key);
    const hasScalarDrivenUiAssertion = [
        'in_combat',
        'current_round',
        'turn_phase',
        'encounter_environment',
    ].some(asserted);
    if (changedKeys.length === 0 && !initiativeChanged && !hasScalarDrivenUiAssertion) {
        return; // blob already current (e.g. canonical push beat us, or values unchanged)
    }
    const changed = (key: string): boolean => changedKeys.includes(key);
    ftDebugLog('ft3d encounter', { phase: 'stats fast-path', encounterId, changedKeys, initiativeChanged });

    // ── Scalar-driven sync, mirroring handleEncounterUpdate piece by piece ──

    const inCombatLive = parseInt(String(encounterField(encounterArr, 'in_combat') ?? '0'), 10) === 1;

    // Keep retained frontend truth current before publishing turn/environment projections.
    if (window.FateTide?.encounterCache instanceof Map) {
        window.FateTide.encounterCache.set(encounterId, encounterArr);
    }

    // Turn creature changed → reset turn timer + GM-controls turn input (mirror of
    // handleEncounterUpdate step 6b). A turn_phase flip is a turn change too, even when
    // the current id happens to survive it (e.g. legendary interlude resume onto the
    // same pending creature after a same-id edge) — the timer must restart either way.
    if ((changed('current_encounter_creature_id_turn') || changed('turn_phase')) && inCombatLive) {
        const defaultSec = getDefaultCreatureTurnTimerSecondsSync();
        const spanRem = document.getElementById('span_creature_turn_timer_remaining');
        if (spanRem) spanRem.innerHTML = String(defaultSec);
        if (typeof window.ftSetTurnTimerState === 'function') {
            window.ftSetTurnTimerState({ remaining: defaultSec }, 'encounter-stats-turn-change');
        }
        window.FateTide = window.FateTide || {};
        window.FateTide.turnTimerWarningIssued = false;
        window.FateTide.turnTimerAutoEnding = false;

        const gmControlsTurnInput = document.getElementById('input_hidden_gm_controls_current_encounter_creature_id_turn') as HTMLInputElement | null;
        if (gmControlsTurnInput) {
            gmControlsTurnInput.value = encounterField(encounterArr, 'current_encounter_creature_id_turn') ?? '';
        }
    }

    // Turn widget / sidelog summary rebuilds — same change-key gating as the canonical
    // handler (NOT light/weather: those keys arrive shelled and are skipped above).
    if (asserted('current_round') || asserted('in_combat') || asserted('turn_phase')) {
        await buildTurnWidgetGMControls();
    }
    if (asserted('in_combat') || asserted('current_round') || asserted('encounter_environment')) {
        getEncounterSettingSummary(encounterArr, null);
    }

    // my_campaign: keep time controls in sync with the active encounter.
    const activeEncounterId = window.ftGetActiveEncounterId?.() || '';
    if (activeEncounterId && encounterId === activeEncounterId) {
        syncMyCampaignTimeControlsToEncounterInCombat(encounterArr);
    }

    ftDebugLog('ft3d encounter', { phase: 'stats fast-path complete', encounterId });
}

window.handleEncounterStatsUpdate = handleEncounterStatsUpdate;

export { handleEncounterStatsUpdate };
